package com.flowyride.core.map

import com.flowyride.core.domain.model.GeoPoint

/**
 * When the planning map moves the camera by itself: only to show a new ride, never because it was edited.
 *
 * - Before any track: the stops being placed are brought into view (a destination picked from the search).
 * - The first track of the ride is framed whole.
 * - From then on, the camera is the rider's: adding a stop, dragging a marker or the line, recomputing (the track
 *   is empty while loading) never moves it.
 *
 * One instance per ride: [RouteMap]'s `framingKey` changes when a ride is loaded or generated.
 */
internal class CameraFraming {
    private var hasFramedTrack = false

    /**
     * @param isVisible whether bounds already lie in the unobscured part of the map.
     * @return the area to frame, or null to leave the camera alone.
     */
    fun boundsToFrame(track: List<GeoPoint>, markers: List<GeoPoint>, isVisible: (Bounds) -> Boolean): Bounds? {
        if (hasFramedTrack) return null
        if (track.isNotEmpty()) return track.bounds()
        val bounds = markers.bounds() ?: return null
        if (isVisible(bounds)) return null
        // A single point is framed as a small area around it: centres it in the visible part at a town-level zoom.
        return if (markers.size == 1) bounds.expandedBy(SINGLE_POINT_MARGIN_DEGREES) else bounds
    }

    /** The camera got there: an interrupted framing is retried on the next change. */
    fun onFramed(track: List<GeoPoint>) {
        if (track.isNotEmpty()) hasFramedTrack = true
    }

    private companion object {
        const val SINGLE_POINT_MARGIN_DEGREES = 0.05
    }
}
